import { User } from "../types";
import { fetchUserAttributes, fetchAuthSession } from "aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import "../amplify"; // Import Amplify configuration
import {
  createUser as createUserMutation,
  updateUser as updateUserMutation,
} from "../../graphql/mutations";
import { getUser as getUserQuery, listUsers } from "../../graphql/queries";
import { UserRole } from "../../API";

// Initialize the API client with proper Amplify configuration
const client = generateClient();

// In-memory cache for user profiles during development (fallback)
// In production, this would be replaced with DynamoDB
const userProfileCache = new Map<string, User>();

/**
 * Get user by username
 */
export async function getUser(username: string): Promise<User | null> {
  try {
    // First check cache
    let foundUser: User | null = null;
    userProfileCache.forEach((user, email) => {
      if (user.username === username || email.split("@")[0] === username) {
        foundUser = user;
      }
    });

    if (foundUser) {
      return foundUser;
    }

    // Try to get current user attributes
    try {
      const attributes = await fetchUserAttributes();

      if (
        attributes.preferred_username === username ||
        attributes.email?.split("@")[0] === username
      ) {
        return await getCurrentUser();
      }
    } catch (authError) {
      console.log("getUser: No valid auth session, checking stored tokens");

      // Check stored tokens
      const storedEmail = sessionStorage.getItem("email");
      const storedUsername = sessionStorage.getItem("username");

      if (
        storedUsername === username ||
        storedEmail?.split("@")[0] === username
      ) {
        // Return cached user if available
        const cachedUser = userProfileCache.get(storedEmail || "");
        if (cachedUser) {
          return cachedUser;
        }

        // Create basic user from stored data
        return {
          id: sessionStorage.getItem("userId") || "temp-id",
          username: storedUsername || "medina",
          email: storedEmail || "medina@theboosterbox.gg",
          firstName: "Christopher",
          lastName: "Medina",
          displayName: "Christopher Medina",
          profilePictureUrl: undefined,
          role: UserRole.PLAYER,
          profileCompleted: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
      }
    }

    return null;
  } catch (error) {
    console.error("Error getting user by username:", error);
    return null;
  }
}

/**
 * Create/Update a user profile in DynamoDB
 */
export async function createUser(
  userData: Omit<User, "id" | "createdAt" | "updatedAt">
): Promise<User | null> {
  console.log("createUser: Starting with userData:", userData);

  try {
    // First, check if user already exists
    const existingUser = await getUserByEmail(userData.email);
    if (existingUser) {
      console.log(
        "createUser: User already exists, returning existing user:",
        existingUser
      );
      return existingUser;
    }

    // First, try to get the current auth session to see if we're authenticated
    console.log("createUser: Checking current auth session...");
    let authSession;
    try {
      authSession = await fetchAuthSession();
      console.log("createUser: Current auth session:", authSession);
    } catch (sessionError) {
      console.log("createUser: No valid auth session, will use stored tokens");
    }

    // Create user object for DynamoDB
    const userInput = {
      username: userData.username,
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      displayName: userData.displayName,
      profilePictureUrl: userData.profilePictureUrl,
      role: userData.role as UserRole,
      profileCompleted: true,
    };

    console.log("createUser: Creating user in DynamoDB:", userInput);

    // Create user in DynamoDB using GraphQL
    // Use API key authentication since auth is not properly configured
    console.log("createUser: Attempting GraphQL mutation with API key auth");
    console.log("createUser: User input:", userInput);

    const result = await client.graphql({
      query: createUserMutation,
      variables: {
        input: userInput,
      },
      authMode: "apiKey",
    });

    console.log("createUser: GraphQL mutation successful:", result);

    const createdUser = result.data.createUser;
    console.log("createUser: User created in DynamoDB:", createdUser);

    // Remove __typename from the user object to match our User type
    const { __typename, ...userWithoutTypename } = createdUser;

    // Convert null to undefined for profilePictureUrl to match our User type
    const userWithCorrectTypes = {
      ...userWithoutTypename,
      profilePictureUrl: userWithoutTypename.profilePictureUrl || undefined,
    };

    // Also cache the user locally for faster access
    userProfileCache.set(userData.email, userWithCorrectTypes);

    return userWithCorrectTypes;
  } catch (error) {
    console.error("createUser: Error creating user:", error);
    console.error("createUser: Error details:", {
      name: error instanceof Error ? error.name : "Unknown",
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : "No stack trace",
    });

    // If user creation fails, check if user already exists
    try {
      const existingUser = await getUserByEmail(userData.email);
      if (existingUser) {
        console.log(
          "createUser: User already exists, returning existing user after error:",
          existingUser
        );
        return existingUser;
      }
    } catch (lookupError) {
      console.error("createUser: Error looking up existing user:", lookupError);
    }

    return null;
  }
}

/**
 * Update user profile in DynamoDB
 */
export async function updateUser(
  id: string,
  updates: Partial<User>
): Promise<User | null> {
  try {
    console.log("updateUser: Updating user with ID:", id);
    console.log("updateUser: Updates:", updates);

    // Get the current user from DynamoDB first using the user ID
    const currentUser = await getUserById(id);
    if (!currentUser) {
      console.error("updateUser: User not found in DynamoDB for ID:", id);
      return null;
    }

    // Prepare update input for DynamoDB
    const updateInput = {
      id: currentUser.id,
      username:
        updates.username !== undefined
          ? updates.username
          : currentUser.username,
      email: updates.email !== undefined ? updates.email : currentUser.email,
      firstName:
        updates.firstName !== undefined
          ? updates.firstName
          : currentUser.firstName,
      lastName:
        updates.lastName !== undefined
          ? updates.lastName
          : currentUser.lastName,
      displayName:
        updates.displayName !== undefined
          ? updates.displayName
          : currentUser.displayName,
      profilePictureUrl:
        updates.profilePictureUrl !== undefined
          ? updates.profilePictureUrl
          : currentUser.profilePictureUrl,
      role:
        updates.role !== undefined
          ? (updates.role as UserRole)
          : (currentUser.role as UserRole),
      profileCompleted:
        updates.profileCompleted !== undefined
          ? updates.profileCompleted
          : currentUser.profileCompleted,
    };

    console.log("updateUser: Updating user in DynamoDB:", updateInput);

    // Update user in DynamoDB using GraphQL
    // Use API key authentication since auth is not properly configured
    const result = await client.graphql({
      query: updateUserMutation,
      variables: {
        input: updateInput,
      },
      authMode: "apiKey",
    });

    const updatedUser = result.data.updateUser;
    console.log(
      "updateUser: Successfully updated user in DynamoDB:",
      updatedUser
    );

    // Remove __typename from the user object to match our User type
    const { __typename, ...userWithoutTypename } = updatedUser;

    // Convert null to undefined for profilePictureUrl to match our User type
    const userWithCorrectTypes = {
      ...userWithoutTypename,
      profilePictureUrl: userWithoutTypename.profilePictureUrl || undefined,
    };

    return userWithCorrectTypes;
  } catch (error) {
    console.error("Error updating user:", error);
    return null;
  }
}

/**
 * Debug function to log current user info
 */
export function debugCurrentUser(): void {
  console.log("=== Current User Debug ===");
  console.log("User Profile Cache Size:", userProfileCache.size);
  console.log("Cached Users:", Array.from(userProfileCache.keys()));
  console.log("==========================");
}

/**
 * Get user by ID from DynamoDB
 */
export async function getUserById(id: string): Promise<User | null> {
  try {
    console.log("getUserById: Looking for user with ID:", id);

    // Query DynamoDB for the user by ID
    // Use API key authentication since auth is not properly configured
    const result = await client.graphql({
      query: getUserQuery,
      variables: {
        id: id,
      },
      authMode: "apiKey",
    });

    const user = result.data.getUser;
    if (!user) {
      console.log("getUserById: User not found in DynamoDB for ID:", id);
      return null;
    }

    console.log("getUserById: Found user in DynamoDB:", user);

    // Remove __typename from the user object to match our User type
    const { __typename, ...userWithoutTypename } = user;

    // Convert null to undefined for profilePictureUrl to match our User type
    const userWithCorrectTypes = {
      ...userWithoutTypename,
      profilePictureUrl: userWithoutTypename.profilePictureUrl || undefined,
    };

    return userWithCorrectTypes;
  } catch (error) {
    console.error("getUserById: Error fetching from DynamoDB:", error);
    return null;
  }
}

/**
 * Get user by email from DynamoDB
 */
export async function getUserByEmail(email: string): Promise<User | null> {
  try {
    console.log("getUserByEmail: Looking for user with email:", email);

    // Get user from DynamoDB using GraphQL
    // Use API key authentication since auth is not properly configured
    const result = await client.graphql({
      query: listUsers,
      variables: {
        filter: {
          email: {
            eq: email,
          },
        },
      },
      authMode: "apiKey",
    });

    const users = result.data.listUsers.items;
    console.log("getUserByEmail: Found users in DynamoDB:", users);

    if (users && users.length > 0) {
      const user = users[0];
      // Remove __typename from the user object to match our User type
      const { __typename, ...userWithoutTypename } = user;

      // Convert null to undefined for profilePictureUrl to match our User type
      const userWithCorrectTypes = {
        ...userWithoutTypename,
        profilePictureUrl: userWithoutTypename.profilePictureUrl || undefined,
      };

      console.log(
        "getUserByEmail: Found user from DynamoDB:",
        userWithCorrectTypes
      );
      return userWithCorrectTypes;
    }

    console.log("getUserByEmail: No user found in DynamoDB for:", email);
    return null;
  } catch (error) {
    console.error("getUserByEmail: Error fetching from DynamoDB:", error);
    return null;
  }
}

/**
 * Get current authenticated user
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    const attributes = await fetchUserAttributes();
    const email = attributes.email;

    if (!email) {
      return null;
    }

    return await getUserByEmail(email);
  } catch (error) {
    console.error("getCurrentUser: Could not fetch from Cognito:", error);
    return null;
  }
}

/**
 * Promote a user to admin role by email
 */
export async function promoteToAdmin(email: string): Promise<User | null> {
  try {
    console.log("promoteToAdmin: Promoting user to admin:", email);

    // Get user from DynamoDB by email
    const user = await getUserByEmail(email);

    if (!user) {
      console.error("promoteToAdmin: User not found in DynamoDB:", email);
      return null;
    }

    console.log("promoteToAdmin: Found user in DynamoDB:", user);

    console.log("promoteToAdmin: Found user:", user);

    // Check if user is already admin
    if (user.role === UserRole.ADMIN) {
      console.log("promoteToAdmin: User is already admin");
      return user;
    }

    // Update the user's role to ADMIN in cache
    const updatedUser = {
      ...user,
      role: UserRole.ADMIN,
      updatedAt: new Date().toISOString(),
    };

    // Update cache
    userProfileCache.set(email, updatedUser);
    console.log("promoteToAdmin: Updated user in cache:", updatedUser);

    // Try to update in DynamoDB, but don't fail if it doesn't work
    try {
      await updateUser(user.id, updatedUser);
      console.log("promoteToAdmin: Successfully updated in DynamoDB");
    } catch (dbError) {
      console.warn(
        "promoteToAdmin: Could not update in DynamoDB, but updated cache:",
        dbError
      );
    }

    console.log(
      "promoteToAdmin: Successfully promoted user to admin:",
      updatedUser
    );
    return updatedUser;
  } catch (error) {
    console.error("promoteToAdmin: Error promoting user to admin:", error);
    return null;
  }
}
